import {ApolloClient, InMemoryCache} from '@apollo/client';
import {ENVIRONMENT, PRIMARY_CHAIN} from '@/util/chains';
import {ChainId} from '@kei.fi/core-ui/constants/contract';
import {persistCache} from 'apollo-cache-persist';

const suffix = PRIMARY_CHAIN === ChainId.ArbitrumOne ? 'arbitrum' : 'sepolia';

export const claimingCache = new InMemoryCache();
export const stakingCache = new InMemoryCache();
export const stakingCacheOLD = new InMemoryCache();
export const tokenCache = new InMemoryCache();

export const subgraphReadyPromises: Promise<void>[] = [Promise.resolve()];

if (typeof window !== 'undefined') {
  subgraphReadyPromises.push(
    persistCache({
      cache: tokenCache,
      storage: window.localStorage as any,
      key: 'token-subgraph-cache',
    }),
    persistCache({
      cache: stakingCache,
      storage: window.sessionStorage as any,
      key: 'staking-subgraph-cache',
    }),
    persistCache({
      cache: stakingCacheOLD,
      storage: window.sessionStorage as any,
      key: 'staking-subgraph-cache-old',
    }),
    persistCache({
      cache: claimingCache,
      storage: window.sessionStorage as any,
      key: 'claiming-subgraph-cache',
    })
  );
}

const API_KEY = process.env.NEXT_PUBLIC_SUBGRAPH_API_KEY;

export const claimingSubgraph = new ApolloClient({
  uri: getURIFromConfig('claiming', {
    staging: {
      42161:
        'https://api.studio.thegraph.com/query/69303/kei-claiming-staging-arbitrum/version/latest',
    },
    production: {
      42161: `https://gateway-arbitrum.network.thegraph.com/api/${API_KEY}/subgraphs/id/BenGEWv5kTQTwm4Rvzs3kjrQVkVeRZApLyEA6e2CDqw1`,
    },
    develop: {
      11155111:
        'https://api.studio.thegraph.com/query/69303/kei-claiming-sepolia/version/latest',
    },
  }),
  cache: claimingCache,
});

export const stakingSubgraph = new ApolloClient({
  uri: getURIFromConfig('staking', {
    staging: {
      42161:
        'https://api.studio.thegraph.com/query/69303/kei-staking-staging-arbitrum/version/latest',
    },
    production: {
      42161: `https://gateway-arbitrum.network.thegraph.com/api/${API_KEY}/subgraphs/id/5TrBLTb3hiUWrDhbfDQAPduSwPshK2cvptkmvUyLT1iW`,
    },
    develop: {
      11155111:
        'https://api.studio.thegraph.com/query/69303/kei-staking-dev-sepolia/version/latest',
    },
  }),
  cache: stakingCache,
});

export const assetManagerSubgraph = new ApolloClient({
  uri: getURIFromConfig('asset-manager', {
    staging: {
      42161:
        'https://api.studio.thegraph.com/query/69303/kei-asset-manager-staging-arbitrum/version/latest',
    },
    production: {
      42161: `https://gateway-arbitrum.network.thegraph.com/api/${API_KEY}/subgraphs/id/QmWLEuJwmdJ3rt8DKU4jjNzbCoSqdRHAQQWFuZFyc5GfxC`,
    },
    develop: {
      11155111:
        'https://api.studio.thegraph.com/query/69303/kei-asset-manager-dev-sepolia/version/latest',
    },
  }),
  cache: stakingCache,
});

export const stakingSubgraphOLD = new ApolloClient({
  uri: {
    42161: `https://gateway-arbitrum.network.thegraph.com/api/${API_KEY}/subgraphs/id/5TrBLTb3hiUWrDhbfDQAPduSwPshK2cvptkmvUyLT1iW`,
    11155111:
      'https://api.studio.thegraph.com/query/69303/kei-staking-sepolia/version/latest',
  }[PRIMARY_CHAIN],
  cache: stakingCacheOLD,
});

export const tokenSubgraph = new ApolloClient({
  uri: getURIFromConfig('token', {
    production: {
      42161: `https://gateway-arbitrum.network.thegraph.com/api/${API_KEY}/subgraphs/id/7y8ue4DnzzLBQPB3dRfJ3Zj7keFKZcZg2rg1buQYCf9M`,
    },
    staging: {
      42161:
        'https://api.studio.thegraph.com/query/69303/kei-token-arbitrum/version/latest',
    },
    develop: {
      11155111:
        'https://api.studio.thegraph.com/query/69303/token-sepolia/version/latest',
    },
  }),
  cache: tokenCache,
});

function getURIFromConfig(
  name: string,
  config: Record<string, Record<string, string>>
) {
  const dev = config.dev ?? config.develop ?? config.development;
  const staging = config.staging ?? config.stage;
  const production = config.production ?? config.prod;

  switch (ENVIRONMENT?.toLowerCase()) {
    case 'dev':
    case 'develop':
    case 'development':
      if (!dev) {
        throw new Error(`Development config not found in config for ${name}`);
      }
      return dev[PRIMARY_CHAIN];
    case 'staging':
    case 'stage':
      if (!staging) {
        throw new Error(`Staging config not found in config for ${name}`);
      }
      return staging[PRIMARY_CHAIN];
    case 'production':
    case 'prod':
      if (!production) {
        throw new Error(`Production config not found in config for ${name}`);
      }
      return production[PRIMARY_CHAIN];
    default:
      throw new Error(
        `Environment ${ENVIRONMENT} not found in config for ${name}`
      );
  }
}
